import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'
import { loadFonts } from './plugins/webfontloader'
import './assets/colors.css'
import { API_ORIGIN } from './config'

loadFonts()

// Todas las llamadas fetch() hacia NUESTRO backend (155+ repartidas en los servicios) deben
// enviar la cookie de sesión para que pueda autenticar al usuario. En vez de editar cada
// llamada, parcheamos window.fetch una sola vez para que incluya `credentials: 'include'`
// SOLO cuando la URL es de nuestra propia API — nunca en llamadas a servicios externos
// (como el clima en api.open-meteo.com), porque mandarles credenciales rompe su CORS
// (responden con Access-Control-Allow-Origin: "*", que el navegador no permite combinar
// con credentials: 'include').
const OWN_API_HOSTS = [new URL(API_ORIGIN).host, 'localhost:4000', '127.0.0.1:4000']

function isOwnApiRequest(input) {
  const url = typeof input === 'string' ? input : input?.url
  if (!url) return false
  try {
    const { host } = new URL(url, window.location.origin)
    return OWN_API_HOSTS.includes(host)
  } catch {
    return false
  }
}

const originalFetch = window.fetch.bind(window)

// Sesión vencida: si el servidor responde que ya no hay sesión, se limpia el estado y se manda al login
// (en vez de dejar la pantalla sin datos hasta que el usuario recargue).
const requestUrl = (input) => (typeof input === 'string' ? input : input?.url) || ''
const isAuthEndpoint = (url) => /\/api\/(auth\/(login|me|activate|forgot-password|reset-password|invitations)|superadmin\/auth)/.test(url)
let handlingExpiry = false

async function handleExpiredSession(response, input) {
  const url = requestUrl(input)
  if (handlingExpiry || isAuthEndpoint(url)) return
  const isSuperadminApi = url.includes('/api/superadmin/')

  // El panel de superadmin responde 404 (no 401) sin sesión; se confirma con /auth/me para no confundirlo con un 404 real.
  if (isSuperadminApi) {
    if (response.status !== 404) return
    const body = await response.clone().json().catch(() => ({}))
    if (body.error !== 'NOT_FOUND') return
    const { adminMe } = await import('./services/superadmin')
    const stillLoggedIn = await adminMe().catch(() => true)
    if (stillLoggedIn) return
    handlingExpiry = true
    const { useSuperadminStore } = await import('./services/superadminStore')
    useSuperadminStore().admin.user = null
    await router.push({ path: '/superadmin/acceso', query: { expired: '1' } }).catch(() => {})
    handlingExpiry = false
    return
  }

  if (response.status !== 401) return
  const body = await response.clone().json().catch(() => ({}))
  if (body.error !== 'NOT_AUTHENTICATED') return
  const current = router.currentRoute.value
  if (current.meta?.public) return
  handlingExpiry = true
  const { useAuthStore } = await import('./services/authStore')
  useAuthStore().state.user = null
  await router.push({ path: '/login', query: { redirect: current.fullPath, expired: '1' } }).catch(() => {})
  handlingExpiry = false
}

window.fetch = async (input, init = {}) => {
  if (!isOwnApiRequest(input)) return originalFetch(input, init)
  const response = await originalFetch(input, { credentials: 'include', ...init })
  if (response.status === 401 || response.status === 404) handleExpiredSession(response, input).catch(() => { handlingExpiry = false })
  return response
}

createApp(App)
  .use(router)
  .use(vuetify)
  .mount('#app')